'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  Users, RefreshCw, Wifi, WifiOff, Calendar, HardDrive,
  Folder, File, ChevronRight, ChevronDown,
  MessageSquare, Send, Bot, X, Loader2, Search
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import dynamic from 'next/dynamic';

const OmniChatPanel = dynamic(() => import('@/components/OmniChatPanel'), { ssr: false });

// ── Types ──
interface GroupMessage {
  id: number; message_id: number; user: string; text: string; timestamp: number; color: string; emoji: string; time: string;
}
interface NasItem { name: string; path: string; type: 'dir' | 'file'; children?: NasItem[]; loading?: boolean; }

// ── Agent metadata ──
const AGENT: Record<string, { emoji: string; color: string; label: string; dash: string }> = {
  poby:  { emoji:'🐻', color:'#ff9a3d', label:'Poby', dash:'poby.snapcodex.com' },
  dony:  { emoji:'🏠', color:'#10b981', label:'Dony', dash:'dony.snapcodex.com' },
  jessi: { emoji:'👩‍💻', color:'#ec4899', label:'Jessi', dash:'jessi.snapcodex.com' },
  shon:  { emoji:'🔧', color:'#ff4d4d', label:'Shon', dash:'shon.snapcodex.com' },
  benny: { emoji:'🍏', color:'#3b82f6', label:'Benny', dash:'benny.snapcodex.com' },
  meety: { emoji:'💻', color:'#a855f7', label:'Meety', dash:'meety.snapcodex.com' },
  tony:  { emoji:'🪟', color:'#eab308', label:'Tony', dash:'tony.snapcodex.com' },
  roony: { emoji:'🦿', color:'#8b5cf6', label:'Roony', dash:'roony.snapcodex.com' },
  rudy:  { emoji:'👑', color:'#f59e0b', label:'Rudy', dash:'rudy.snapcodex.com' },
  user:  { emoji:'👤', color:'#6b7280', label:'대표님', dash:'' },
  claude:{ emoji:'🤖', color:'#6366f1', label:'Claude', dash:'' },
  codex: { emoji:'⌘', color:'#14b8a6', label:'Codex', dash:'' },
};

function inferAgent(text:string, origUser:string): string {
  const user = (origUser || 'user').toLowerCase();
  
  // 🏷️ Priority 1: System Prompt source tag (e.g. "🍏 Benny[=OpenClaw] @ ben-macbookair-1")
  const sourceTag = text.match(/([🐻🔧👩‍💻🏠🍏💻🪟🦿👑🤖⌘])\s*(Poby|Jessi|Shon|Benny|Dony|Meety|Tony|Roony|Rudy|Codex|Claude)\s*\[=/u);
  if (sourceTag) {
    const name = sourceTag[2].toLowerCase();
    if (AGENT[name]) return name;
    if (name === 'codex') return 'codex';
    if (name === 'claude') return 'claude';
  }

  // 🏷️ Priority 2: direct name match from user field
  if (user.includes('snapgrid_codex') || user.includes('codex')) return 'codex';
  if (user.includes('claude')) return 'claude';
  if (AGENT[user]) return user;
  if (!text) return user;

  // 🏷️ Priority 3: hostname extraction from "@ hostname" in text
  const hostMatch = text.match(/@\s*([a-z][a-z0-9._-]+)/i);
  if (hostMatch) {
    const h = hostMatch[1].toLowerCase();
    if (h.startsWith('dony')) return 'dony';
    if (h.startsWith('poby')) return 'poby';
    if (h.startsWith('shon')||h.includes('cindy')) return 'shon';
    if (h.startsWith('jessi')||h.startsWith('gyungchul')) return 'jessi';
    if (h.startsWith('ben')) return 'benny';
    if (h.startsWith('meety')) return 'meety';
    if (h.startsWith('tony')) return 'tony';
    if (h.startsWith('roony')) return 'roony';
    if (h.startsWith('rudy')) return 'rudy';
  }

  // 🏷️ Priority 4: emoji + name pattern (fallback)
  const emojiMatch = text.match(/[🐻🔧👩‍💻🏠🍏💻🪟🦿👑]\s*(Poby|Jessi|Shon|Benny|Dony|Meety|Tony|Roony|Rudy)/u);
  if (emojiMatch) return emojiMatch[1].toLowerCase();
  const nameMatch = text.match(/^(Poby|Jessi|Shon|Benny|Dony|Meety|Tony|Roony|Rudy)\s*\(|:/m);
  if (nameMatch && nameMatch[1]) return nameMatch[1].toLowerCase();
  if (/Codex|\[Codex\]/i.test(text)) return 'codex';
  if (/Claude|\[Claude\]|claude_bot/i.test(text)) return 'claude';
  if (text.length > 300 && /대표님|보고드립니다|리포트|linter|과장표현/.test(text)) return 'poby';
  return user;
}

const LEGEND: { labels: string[]; color: string }[] = [
  { labels: ['제안','PT','프레젠','RFP','PQ'], color: 'bg-[#e74c3c]' },
  { labels: ['도면','디자인','컬러','색채','설계도서'], color: 'bg-[#ec7063]' },
  { labels: ['내역','원가','BOQ','적산','단가'], color: 'bg-gray-800' },
  { labels: ['회의','취합','협의','자문'], color: 'bg-[#af7ac5]' },
  { labels: ['견적','입찰','투찰'], color: 'bg-[#a9dfbf]' },
  { labels: ['납품','제출','업로드','CG','동영상','렌더'], color: 'bg-[#58d68d]' },
  { labels: ['정산','계약','청구'], color: 'bg-[#f5b041]' },
  { labels: ['보고','연락','공문'], color: 'bg-[#5dade2]' },
  { labels: ['경영','일반','인사','총무'], color: 'bg-[#a3e4d7]' },
];

function getColorClass(title: string): string {
  for (const leg of LEGEND) if (leg.labels.some(l=>title.includes(l))) return leg.color;
  return 'bg-[#5dade2]';
}

// ── NAS Explorer ──
interface NasExplorerProps {
  isDark: boolean;
  onSelectFile: (path: string) => void;
  selectedFile: string | null;
  onItemContextMenu: (e: React.MouseEvent, path: string, type: 'dir' | 'file') => void;
}

function NasExplorer({ isDark, onSelectFile, selectedFile, onItemContextMenu }: NasExplorerProps) {
  const [tree, setTree] = useState<NasItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleDir = useCallback(async (item: NasItem) => {
    const key = item.path;
    const next = new Set(expanded);
    if (next.has(key)) { next.delete(key); setExpanded(next); return; }
    next.add(key); setExpanded(next);
    if (!item.children) {
      item.loading = true; setTree([...tree]);
      try {
        const res = await fetch(`/api/nas/browse?path=${encodeURIComponent(item.path)}`);
        const data = await res.json();
        item.children = (data.items || data.files || []).map((e:any) => ({
          name: e.name, path: e.path || e.name, type: e.type === 'directory' ? 'dir' : 'file'
        })).sort((a:NasItem,b:NasItem) => (b.type==='dir'?1:0)-(a.type==='dir'?1:0) || a.name.localeCompare(b.name));
      } catch { item.children = []; }
      item.loading = false; setTree([...tree]);
    }
  }, [expanded, tree]);

  useEffect(() => {
    setLoading(true);
    fetch('/api/nas/browse?path=').then(r=>r.json()).then(d=>{
      const items = (d.items||[]).map((e:any)=>({
        name:e.name, path:e.path||e.name, type:e.type==='directory'?'dir':'file'
      })).sort((a:NasItem,b:NasItem)=>(b.type==='dir'?1:0)-(a.type==='dir'?1:0)||a.name.localeCompare(b.name));
      setTree(items);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const renderItem = (item: NasItem, depth: number) => {
    const isSelected = selectedFile === item.path;
    const isDir = item.type === 'dir';

    return (
      <div key={item.path}>
        <button
          onClick={() => isDir ? toggleDir(item) : onSelectFile(item.path)}
          onContextMenu={(e) => {
            e.preventDefault();
            onItemContextMenu(e, item.path, item.type);
          }}
          className={`w-full flex items-center gap-1.5 py-1.5 px-2 text-xs rounded transition text-left ${
            isSelected
              ? (isDark ? 'bg-cyan-500/20 text-cyan-300 font-semibold' : 'bg-cyan-50 text-cyan-800 font-semibold border border-cyan-200/50')
              : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-black/5 text-gray-700')
          }`}
          style={{ paddingLeft: 8 + depth * 16 }}
        >
          {isDir ? (
            expanded.has(item.path) ? <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50"/> : <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-50"/>
          ) : <span className="w-3.5"/>}
          {isDir ? (
            <Folder className={`w-4 h-4 shrink-0 ${isDark ? 'text-amber-500' : 'text-amber-600'}`}/>
          ) : <File className="w-4 h-4 shrink-0 opacity-50" />}
          <span className="truncate flex-1">{item.name}</span>
          {item.loading && <Loader2 className="w-3 h-3 animate-spin ml-auto"/>}
        </button>
        {expanded.has(item.path) && item.children?.map(ch => renderItem(ch, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0a12]/50 border-r border-white/5' : 'bg-gray-50 border-r border-gray-200'}`}>
      <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <HardDrive className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`}/>
          <span className="text-xs font-semibold">NAS 보관함</span>
        </div>
        {loading && <Loader2 className="w-3 h-3 animate-spin opacity-50"/>}
      </div>
      <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {tree.map(i=>renderItem(i,0))}
      </div>
    </div>
  );
}

// ── Main Page ──
export default function SnapGRIDGroupChatPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [lastId, setLastId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(true);
  const [sseActive, setSseActive] = useState(false);
  const [sseFallback, setSseFallback] = useState(false);
  const sseErrorCount = useRef(0);
  const [showNas, setShowNas] = useState(true);
  const [showOmniChat, setShowOmniChat] = useState(true);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>|null>(null);

  // ── Context Menu & Toast states ──
  const [menuState, setMenuState] = useState<{
    visible: boolean; x: number; y: number; path: string; type: 'dir' | 'file' | 'chip'
  }>({ visible: false, x: 0, y: 0, path: '', type: 'file' });
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });

  const showToast = useCallback((msg: string) => {
    setToast({ message: msg, visible: true });
    const timer = setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 2200);
    return () => clearTimeout(timer);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, path: string, type: 'dir' | 'file' | 'chip') => {
    e.preventDefault();
    const menuWidth = 220;
    const menuHeight = 200;
    let spawnX = e.clientX;
    let spawnY = e.clientY;
    
    if (typeof window !== 'undefined') {
      if (spawnX + menuWidth > window.innerWidth) spawnX = window.innerWidth - menuWidth - 8;
      if (spawnY + menuHeight > window.innerHeight) spawnY = window.innerHeight - menuHeight - 8;
    }
    
    setMenuState({ visible: true, x: spawnX, y: spawnY, path, type });
  }, []);

  // ── Cmd/Ctrl + Shift + C Keyboard Shortcut ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        if (selectedFile) {
          e.preventDefault();
          navigator.clipboard.writeText(selectedFile);
          showToast(`컨텍스트 경로가 복사되었습니다: ${selectedFile}`);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFile, showToast]);

  const scroll = useCallback((smooth=true) => setTimeout(()=>endRef.current?.scrollIntoView({behavior:smooth?'smooth':'instant'}),100),[]);

  const fetchMsgs = useCallback(async (init=false) => {
    try {
      const since = init ? 0 : lastId;
      const res = await fetch(`/api/chat/proxy_messages?limit=100${since>0?`&since_id=${since}`:''}`);
      if (!res.ok) throw new Error('');
      const data = await res.json();
      if (!data.messages) {
        if (init) setLoading(false);
        return;
      }
      if (init) { setMessages(data.messages); setLoading(false); }
      else if (data.messages.length>0) {
        setMessages(prev=>{
          const ids = new Set(prev.map(m=>m.id));
          const fresh = data.messages.filter((m:GroupMessage)=>!ids.has(m.id));
          return fresh.length?[...prev,...fresh].slice(-300):prev;
        });
      }
      if (data.messages.length>0) {
        // messages are DESC (newest first), track newest id
        setLastId(data.messages[0].id);
      }
      setConnected(true);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setConnected(false);
      if (init) setLoading(false);
    }
  }, [lastId]);

  const connectSSE = useCallback((startId: number) => {
    if (sseFallback) return null;

    const targetUrl = `/api/chat/events?lastEventId=${startId}`;
    console.log('[SSE] Attempting connection to:', targetUrl);
    const es = new EventSource(targetUrl);

    es.onopen = () => {
      console.log('[SSE] Connection successfully established.');
      setSseActive(true);
      setConnected(true);
      sseErrorCount.current = 0;
    };

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (!msg || !msg.id) return;

        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, msg].slice(-300);
        });

        setLastId(msg.id);
      } catch (err) {
        console.error('[SSE] Failed to parse message data:', err);
      }
    };

    es.onerror = (e) => {
      console.warn('[SSE] EventSource encountered connection error/closed:', e);
      setSseActive(false);
      es.close();

      sseErrorCount.current += 1;
      if (sseErrorCount.current >= 3) {
        console.error('[SSE] 3 consecutive failures. Permanently falling back to active 5s polling.');
        setSseFallback(true);
        showToast('실시간 채널 혼잡으로 일반 폴링 동기화로 전환합니다.');
      } else {
        // 3초 후 점진적 백오프 재연결 시도
        setTimeout(() => connectSSE(lastId), 3000);
      }
    };

    return es;
  }, [lastId, sseFallback, showToast]);

  useEffect(() => {
    fetchMsgs(true);
  }, []);

  useEffect(() => {
    let es: EventSource | null = null;

    if (!sseFallback && lastId > 0) {
      es = connectSSE(lastId);
    }

    return () => {
      if (es) {
        es.close();
        console.log('[SSE] Terminated EventSource connection.');
      }
    };
  }, [sseFallback, lastId === 0, connectSSE]);

  useEffect(() => {
    if (sseFallback) {
      console.log('[Sync] Initializing 2s Polling fallback routine.');
      pollRef.current = setInterval(() => fetchMsgs(false), 2000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [sseFallback, fetchMsgs]);
  useEffect(()=>{scroll(messages.length<=20);},[messages.length,scroll]);

  const toDate = (m: GroupMessage) => {
    if (m.time) return new Date(m.time);
    if (m.timestamp) {
      return new Date(m.timestamp < 1e12 ? m.timestamp * 1000 : m.timestamp);
    }
    return new Date();
  };
  const fmtTime = (m: GroupMessage) => toDate(m).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  const fmtDate = (m: GroupMessage) => toDate(m).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });

  // Group by date
  const groups = messages.reduce<{date:string;msgs:GroupMessage[]}[]>((acc,m)=>{
    const dl=fmtDate(m); const last=acc[acc.length-1];
    last&&last.date===dl?last.msgs.push(m):acc.push({date:dl,msgs:[m]});return acc;
  },[]);

  const handleSend = async () => {
    const text = input.trim(); if(!text||sending)return;
    setSending(true); setInput('');
    const now = new Date();
    const tempId = Date.now();
    
    // Optimistically add message to UI
    setMessages(prev=>[...prev,{id:tempId,message_id:0,user:'user',text,timestamp:now.getTime(),color:'#6b7280',emoji:'👤',time:now.toISOString()}]);
    
    try {
      const res = await fetch('/api/groupchat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: '-5157354011', text })
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(`전송 실패: ${data.error || '잠시 후 다시 시도해주세요.'}`);
        setMessages(prev => prev.filter(m => m.id !== tempId));
      } else {
        const data = await res.json();
        if (data.ok === false) {
          showToast(`전송 실패: ${data.error || '잠시 후 다시 시도해주세요.'}`);
          setMessages(prev => prev.filter(m => m.id !== tempId));
        }
      }
    } catch (err) {
      console.error('Failed to send groupchat message:', err);
      showToast('네트워크 오류: 메시지 전송에 실패했습니다.');
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      setSending(false);
    }
  };

  const headerBg = isDark?'bg-gray-950/80 backdrop-blur-md border-white/10':'bg-white/80 backdrop-blur-md border-gray-200';
  const chatBg = isDark?'bg-black':'bg-[#f9fafb]';
  const inputBg = isDark?'bg-gray-800':'bg-white border border-gray-200';

  if (loading) return (
    <div className={`min-h-screen pt-16 flex items-center justify-center ${chatBg}`}>
      <Loader2 className="w-6 h-6 animate-spin text-gray-400"/>
    </div>
  );

  return (
    <div className={`h-screen flex flex-col ${chatBg} text-${isDark?'white':'gray-900'} font-sans overflow-hidden`}>
      {/* ── Top Nav ── */}
      <nav className={`fixed top-0 left-0 right-0 z-40 h-12 sm:h-14 border-b flex items-center justify-between px-3 sm:px-4 ${headerBg}`}>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link href="/scheduler" className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition ${isDark?'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20':'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>
            <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5"/> <span className="hidden sm:inline">스케줄러</span>
          </Link>
          <Link href="/agent" className={`hidden sm:flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition ${isDark?'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20':'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}>
            <Bot className="w-3 h-3 sm:w-3.5 sm:h-3.5"/> 에이전트
          </Link>
          <button onClick={()=>setShowNas(!showNas)}
            className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition ${showNas?(isDark?'bg-amber-500/20 text-amber-300':'bg-amber-200 text-amber-800'):(isDark?'hover:bg-white/10 text-gray-400':'hover:bg-gray-100 text-gray-600')}`}>
            {showNas?<X className="w-3 h-3 sm:w-3.5 sm:h-3.5"/>:<HardDrive className="w-3 h-3 sm:w-3.5 sm:h-3.5"/>}
            <span className="hidden sm:inline">NAS</span>
          </button>
          <button onClick={()=>setShowOmniChat(!showOmniChat)}
            className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-medium transition ${showOmniChat?(isDark?'bg-cyan-500/20 text-cyan-300':'bg-cyan-100 text-cyan-800'):(isDark?'hover:bg-white/10 text-gray-400':'hover:bg-gray-100 text-gray-600')}`}>
            {showOmniChat?<X className="w-3 h-3 sm:w-3.5 sm:h-3.5"/>:<MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5"/>}
            <span className="hidden sm:inline">옴니챗</span>
          </button>
        </div>
        <div className="flex items-center gap-1">
          {Object.entries(AGENT).filter(([k])=>k!=='user'&&k!=='claude'&&k!=='codex').map(([key,a])=>(
            <a key={key} href={`https://${a.dash}`} target="_blank" rel="noopener"
              className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs transition hover:scale-110"
              style={{background:a.color+'22'}} title={a.label}>
              {a.emoji}
            </a>
          ))}
          <span className="hidden sm:inline-flex items-center gap-1.5 ml-2">
            {!connected ? (
              <span className="flex items-center gap-1 text-red-400 font-semibold bg-red-500/10 px-2 py-0.5 rounded-full text-[10px] border border-red-500/20 animate-pulse">
                <WifiOff className="w-3 h-3" />
                OFFLINE
              </span>
            ) : sseActive ? (
              <span className="flex items-center gap-1 text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full text-[10px] border border-emerald-500/20 shadow-[0_0_8px_rgba(16,185,129,0.35)] animate-pulse">
                <Wifi className="w-3 h-3 text-emerald-400" />
                SSE LIVE
              </span>
            ) : sseFallback ? (
              <span className="flex items-center gap-1 text-amber-400 font-semibold bg-amber-500/10 px-2 py-0.5 rounded-full text-[10px] border border-amber-500/20">
                <Wifi className="w-3 h-3 text-amber-400" />
                POLL BACKUP
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sky-400 font-medium bg-sky-500/10 px-2 py-0.5 rounded-full text-[10px] border border-sky-500/20 animate-pulse">
                <Wifi className="w-3 h-3 text-sky-400" />
                CONNECTING
              </span>
            )}
          </span>
        </div>
      </nav>

      {/* ── Body ── */}
      <div className="flex-1 flex pt-12 sm:pt-14 overflow-hidden h-[calc(100vh-3.5rem)]">
        {/* Left Side: NAS Sidebar */}
        {showNas && (
          <div className="w-64 shrink-0 hidden md:block h-full overflow-hidden">
            <NasExplorer 
              isDark={isDark} 
              onSelectFile={setSelectedFile} 
              selectedFile={selectedFile} 
              onItemContextMenu={(e, path, type) => handleContextMenu(e, path, type)}
            />
          </div>
        )}

        {/* Center: Group Chat Column */}
        <div className="flex-1 flex flex-col min-w-0 h-full border-r border-gray-200 dark:border-white/5">
          {/* Header */}
          <div className={`flex items-center gap-3 px-4 py-2.5 border-b shrink-0 ${isDark?'border-white/10':'border-gray-200'}`}>
            <div className={`p-1.5 rounded-lg ${isDark?'bg-emerald-500/20':'bg-emerald-100'}`}>
              <Users className={`w-4 h-4 ${isDark?'text-emerald-400':'text-emerald-600'}`}/>
            </div>
            <div>
              <h1 className="text-sm font-bold">SnapGRID 단체톡</h1>
              <p className={`text-[10px] ${isDark?'text-gray-500':'text-gray-400'}`}>7 members · online</p>
            </div>
            <button onClick={()=>fetchMsgs(true)} className={`ml-auto p-1.5 rounded-lg ${isDark?'hover:bg-white/10':'hover:bg-gray-100'}`}>
              <RefreshCw className="w-3.5 h-3.5 text-gray-400"/>
            </button>
          </div>

          {/* Selected File Banner context */}
          {selectedFile && (
            <div 
              onContextMenu={(e) => handleContextMenu(e, selectedFile, 'chip')}
              title="우클릭 시 경로 복사 메뉴가 열립니다"
              className={`px-4 py-1.5 text-[11px] font-medium border-b flex items-center gap-2 shrink-0 select-none cursor-context-menu ${isDark ? 'bg-cyan-950/30 border-white/5 text-cyan-300' : 'bg-cyan-50/50 border-gray-200 text-cyan-800'}`}
            >
              <File className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
              <span className="truncate">선택된 컨텍스트: <span className="font-bold">{selectedFile}</span></span>
              <button onClick={() => setSelectedFile(null)} className="ml-auto p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-gray-400 hover:text-red-500 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {groups.map((g,gi)=>(
              <div key={gi}>
                <div className="flex justify-center mb-3">
                  <span className={`text-[10px] px-3 py-1 rounded-full font-medium ${isDark?'bg-gray-800 text-gray-500':'bg-gray-200 text-gray-500'}`}>{g.date}</span>
                </div>
                {g.msgs.map((m,mi)=>{
                  const agent = inferAgent(m.text, m.user||'');
                  const a = AGENT[agent]||AGENT.user;
                  const isSelf = agent==='user';
                  const prev = mi>0&&inferAgent(g.msgs[mi-1].text,g.msgs[mi-1].user||'')===agent;
                  const colorBar = getColorClass(m.text||'');
                  return (
                    <div key={m.id} className={`flex items-start gap-2 ${isSelf?'flex-row-reverse':''} ${prev?'mt-0.5':'mt-2'}`}>
                      {!isSelf&&(
                        prev?<div className="w-7 shrink-0"/>:
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 mt-0.5" style={{background:a.color+'22'}}>{a.emoji}</div>
                      )}
                      <div className={`max-w-[72%] ${isSelf?'flex flex-col items-end':''}`}>
                        {!prev&&!isSelf&&<span className="text-[10px] font-semibold ml-1 mb-0.5 block" style={{color:a.color}}>{a.label}</span>}
                        <div className="flex items-stretch">
                          {!isSelf&&<div className={`w-1 rounded-l-md shrink-0 ${colorBar}`}/>}
                          <div className={`px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${isSelf?'bg-emerald-600 text-white rounded-tr-sm':'rounded-tl-sm '+inputBg}`}>
                            {m.text}
                          </div>
                        </div>
                        <span className={`text-[10px] mt-0.5 ${isDark?'text-gray-600':'text-gray-400'}`}>{fmtTime(m)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={endRef}/>
          </div>

          {/* Input area */}
          <div className={`px-4 py-3 border-t shrink-0 ${isDark?'border-white/10':'border-gray-200'}`}>
            <div className="flex gap-2 items-end max-w-3xl mx-auto">
              <input
                value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleSend();}}}
                placeholder="메시지를 입력하세요..."
                className={`flex-1 rounded-xl px-4 py-2.5 text-sm outline-none transition ${inputBg} ${isDark?'text-white placeholder-gray-500':'text-gray-900 placeholder-gray-400'}`}
              />
              <button onClick={handleSend} disabled={!input.trim()||sending}
                className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 transition disabled:opacity-40 hover:bg-emerald-600">
                {sending?<Loader2 className="w-4 h-4 animate-spin"/>:<Send className="w-4 h-4"/>}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side: OmniChat Sidebar */}
        {showOmniChat && (
          <div className="w-[430px] shrink-0 hidden xl:block h-full overflow-hidden bg-white dark:bg-[#0a0a12]">
            <OmniChatPanel
              compact={true}
              className="h-full border-0 rounded-none shadow-none bg-transparent"
              selectedFile={selectedFile ?? undefined}
              projectId="default"
              placeholder={selectedFile ? `${selectedFile.split('/').pop()}에 대해 질문하세요...` : "건축 설계 관련 RAG 질문을 입력하세요..."}
            />
          </div>
        )}
      </div>

      {/* ── Context Menu Overlay ── */}
      {menuState.visible && (
        <>
          <div
            className="fixed inset-0 z-50 cursor-default"
            onClick={() => setMenuState(prev => ({ ...prev, visible: false }))}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenuState(prev => ({ ...prev, visible: false }));
            }}
          />
          <div
            className={`fixed z-[60] min-w-[210px] py-1.5 rounded-xl shadow-2xl border text-[11px] font-medium overflow-hidden backdrop-blur-lg transition-all duration-100 ${
              isDark 
                ? 'bg-slate-900/95 border-white/10 text-gray-200' 
                : 'bg-white/95 border-gray-200/90 text-gray-800'
            }`}
            style={{ top: menuState.y, left: menuState.x }}
          >
            <div className={`px-3 py-1 text-[9px] font-bold uppercase tracking-wider opacity-40 select-none ${isDark ? 'text-white' : 'text-black'}`}>
              {menuState.type === 'dir' ? '폴더 옵션' : menuState.type === 'chip' ? '컨텍스트 옵션' : '파일 옵션'}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(menuState.path);
                showToast('상대 경로가 복사되었습니다!');
                setMenuState(prev => ({ ...prev, visible: false }));
              }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                isDark ? 'hover:bg-cyan-500/20 hover:text-cyan-300' : 'hover:bg-cyan-50 hover:text-cyan-800'
              }`}
            >
              <span>상대 경로 복사</span>
              <span className={`ml-auto text-[9px] opacity-40 font-mono ${isDark ? 'text-cyan-300' : 'text-cyan-800'}`}>
                {menuState.path.length > 20 ? menuState.path.substring(0, 17) + '...' : menuState.path}
              </span>
            </button>
            <button
              onClick={() => {
                const parts = menuState.path.split('/');
                parts.pop();
                const parentPath = parts.join('/') || '/';
                navigator.clipboard.writeText(parentPath);
                showToast('상위 폴더 경로가 복사되었습니다!');
                setMenuState(prev => ({ ...prev, visible: false }));
              }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                isDark ? 'hover:bg-cyan-500/20 hover:text-cyan-300' : 'hover:bg-cyan-50 hover:text-cyan-800'
              }`}
            >
              <span>상위 폴더 경로 복사</span>
            </button>
            <button
              onClick={() => {
                const absPath = `/Volumes/snapgrid/${menuState.path}`;
                navigator.clipboard.writeText(absPath);
                showToast('NAS 절대 경로가 복사되었습니다!');
                setMenuState(prev => ({ ...prev, visible: false }));
              }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 transition-colors ${
                isDark ? 'hover:bg-cyan-500/20 hover:text-cyan-300' : 'hover:bg-cyan-50 hover:text-cyan-800'
              }`}
            >
              <span>NAS 절대 경로 복사</span>
            </button>
            <div className={`my-1 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} />
            <button
              onClick={() => {
                showToast('탐색기/Finder 실행은 NAS 백엔드 검증 중입니다.');
                setMenuState(prev => ({ ...prev, visible: false }));
              }}
              className={`w-full text-left px-3 py-2 transition-colors opacity-70 hover:opacity-100 ${
                isDark ? 'hover:bg-white/5 text-gray-400 hover:text-white' : 'hover:bg-gray-50 text-gray-500 hover:text-gray-900'
              }`}
            >
              탐색기/Finder에서 보기
            </button>
            <button
              onClick={() => {
                showToast('터미널 실행은 SSH 터널 상태를 체크 중입니다.');
                setMenuState(prev => ({ ...prev, visible: false }));
              }}
              className={`w-full text-left px-3 py-2 transition-colors opacity-70 hover:opacity-100 ${
                isDark ? 'hover:bg-white/5 text-gray-400 hover:text-white' : 'hover:bg-gray-50 text-gray-500 hover:text-gray-900'
              }`}
            >
              터미널에서 열기
            </button>
          </div>
        </>
      )}

      {/* ── Glassmorphic Toast Notification ── */}
      {toast.visible && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 text-xs rounded-full font-semibold border bg-emerald-500 text-white shadow-xl flex items-center gap-2 border-emerald-400/30 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
